import { useState, useCallback } from 'react';
import { trpc } from '@org/clients/school';

interface UploadState {
  progress: number;
  status: 'idle' | 'requesting-urls' | 'uploading' | 'composing' | 'success' | 'error';
  error?: string;
  mediaUrl?: string;
  mediaId?: string;
  currentChunk?: number;
  totalChunks?: number;
}

// 5MB chunk size (recommended minimum is 5MB for GCS)
const CHUNK_SIZE = 100 * 1024 * 1024;

export const useMediaUpload = () => {
  const [uploadState, setUploadState] = useState<UploadState>({
    progress: 0,
    status: 'idle'
  });

  const getSignedUrlMutation = trpc.media.getSignedUrl.useMutation();
  const composeChunksMutation = trpc.media.composeChunks.useMutation();

  const resetUpload = useCallback(() => {
    setUploadState({
      progress: 0,
      status: 'idle'
    });
  }, []);

  // Helper function - doesn't need useCallback (only used internally)
  const uploadSingleFile = async (file: File) => {
    setUploadState({
      progress: 0,
      status: 'requesting-urls'
    });

    // Get signed URL for single file
    const signedUrlData = await getSignedUrlMutation.mutateAsync({
      filename: file.name,
      contentType: file.type,
      fileSize: file.size
    });

    const { signedUrl, mediaId, finalMediaUrl } = signedUrlData;

    // Upload file
    setUploadState(prev => ({ ...prev, status: 'uploading' }));

    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }

    // Success
    setUploadState({
      progress: 100,
      status: 'success',
      mediaId,
      mediaUrl: finalMediaUrl
    });

    return { mediaId, mediaUrl: finalMediaUrl };
  };

  // Helper function - doesn't need useCallback (only used internally)
  const uploadChunkedFile = async (file: File, totalChunks: number) => {
    const chunkPaths: string[] = [];
    let mediaId = '';

    setUploadState({
      progress: 0,
      status: 'requesting-urls',
      totalChunks
    });

    // Upload each chunk
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      console.log(`Uploading chunk ${chunkIndex + 1}/${totalChunks} (${(chunk.size / 1024 / 1024).toFixed(2)}MB)`);

      // Get signed URL for this chunk
      const signedUrlData = await getSignedUrlMutation.mutateAsync({
        filename: file.name,
        contentType: file.type,
        fileSize: chunk.size,
        chunkIndex,
        totalChunks
      });

      if (chunkIndex === 0) {
        mediaId = signedUrlData.mediaId;
      }

      // Upload chunk
      setUploadState(prev => ({
        ...prev,
        status: 'uploading',
        currentChunk: chunkIndex + 1,
        progress: Math.round((chunkIndex / totalChunks) * 90)
      }));

      const uploadResponse = await fetch(signedUrlData.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: chunk
      });

      if (!uploadResponse.ok) {
        throw new Error(`Chunk ${chunkIndex} upload failed: ${uploadResponse.statusText}`);
      }

      // Store chunk path for compose
      if (signedUrlData.chunkPath) {
        chunkPaths.push(signedUrlData.chunkPath);
      }

      console.log(`Chunk ${chunkIndex + 1}/${totalChunks} uploaded successfully`);
    }

    // Compose all chunks into final file
    setUploadState(prev => ({
      ...prev,
      status: 'composing',
      progress: 95
    }));

    console.log(`Composing ${chunkPaths.length} chunks into final file...`);

    const composeResult = await composeChunksMutation.mutateAsync({
      filename: file.name,
      contentType: file.type,
      chunkPaths,
      mediaId
    });

    // Success
    setUploadState({
      progress: 100,
      status: 'success',
      mediaId: composeResult.mediaId,
      mediaUrl: composeResult.mediaUrl
    });

    console.log('File upload complete!', composeResult.mediaUrl);

    return {
      mediaId: composeResult.mediaId,
      mediaUrl: composeResult.mediaUrl
    };
  };

  // Main upload function - needs useCallback (exposed to consumers)
  const uploadMedia = useCallback(async (file: File) => {
    try {
      // Reset state for new upload
      setUploadState({
        progress: 0,
        status: 'idle'
      });

      // Calculate number of chunks needed
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const useChunking = totalChunks > 1;

      console.log(`Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) in ${totalChunks} chunk(s)`);

      // Single file upload (< 5MB)
      if (!useChunking) {
        return await uploadSingleFile(file);
      }

      // Chunked upload (>= 5MB)
      return await uploadChunkedFile(file, totalChunks);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }));
      throw error;
    }
  }, [getSignedUrlMutation, composeChunksMutation]);

  return {
    uploadState,
    uploadMedia,
    resetUpload
  };
};